
import { defineComponent, ref, computed, watch } from "vue";
import { useStore } from "vuex";
import { Actions } from "@/store/enums/StoreEnums";
import FormLoader from "@/components/FormLoader.vue";
import FormPanel from "@/components/form/parts/FormPanel.vue";
import SideBar from "./parts/SideBarVue.vue";

export default defineComponent({
  name: "FormComponent",
  components: { FormLoader, FormPanel, SideBar },
  props: {
    routeKey: { type: String, required: true },
    id: { type: String, required: true },
    parentID: { type: Number, required: false },
  },
  setup(props) {
    const store = useStore();
    const localValues = ref<any>({});
    const submitted = ref(false);
    const newTier = ref<number | null>(null);

    // getters
    const getFormMappingColumns = computed(() => store.getters.getFormMappingColumns({ key: props.routeKey }));
    const item = computed(() => store.getters.getDynamicEntity({ key: props.routeKey }));
    const sideBar = computed(() => store.getters.getFormSideBar({ key: props.routeKey }));
    const getRelationships = computed(() => store.getters.getRelationships({ key: props.routeKey }));
    const getValidationSchema = computed(() => store.getters.getValidationSchema({ key: props.routeKey }));
    const getIsPage = computed(() => store.getters.getIsPage({ key: props.routeKey }));
    const getValues = computed(() => store.getters.getFormValues({ key: props.routeKey }));
    const getFormWithImage = computed(() => store.getters.getFormWithImage({ key: props.routeKey }));

    // init
    store.dispatch(Actions.SETUP_FORM, { key: props.routeKey, attrKey: props.routeKey, id: props.id });
    localValues.value = getValues.value;

    watch(getValues, () => {
      submitted.value = false;
      localValues.value = getValues.value;
    });

    // handlers
    const submit = () => {
      if (getFormWithImage.value) {
        const formData = new FormData() as any;

        const appendValue = (key: string, val: any) => {
          if (val === null || val === undefined || val === '') return;

          if (val instanceof Date) {
            formData.append(key, val.toISOString());
            return;
          }

          // files / blobs
          if (val instanceof File || val instanceof Blob) {
            formData.append(key, val);
            return;
          }

          // arrays
          if (Array.isArray(val)) {
            // Drop wholly-empty object rows so indices stay contiguous — a gap
            // (e.g. an empty row at index 0) makes ASP.NET stop binding the list
            // and silently truncates the rest.
            const isEmptyField = (v: any) =>
              v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0);
            const isEmptyObjectRow = (item: any) =>
              item && typeof item === 'object' && !(item instanceof File) && !(item instanceof Blob) &&
              Object.values(item).every(isEmptyField);
            const effective = val.filter((item) => !isEmptyObjectRow(item));

            effective.forEach((item, idx) => {
              const arrayKey = `${key}[${idx}]`;
              if (item && typeof item === "object" && !(item instanceof File) && !(item instanceof Blob)) {
                Object.entries(item).forEach(([childKey, childVal]) =>
                  appendValue(`${arrayKey}.${childKey}`, childVal)
                );
              } else {
                appendValue(arrayKey, item);
              }
            });
            return;
          }

          // plain objects
          if (typeof val === "object") {
            Object.entries(val).forEach(([childKey, childVal]) =>
              appendValue(`${key}.${childKey}`, childVal)
            );
            return;
          }

          // primitives
          formData.append(key, val);

          // keep image path alongside file-less submissions (helps server preserve existing image)
          if (typeof val === "string" && key.toLowerCase().includes("image")) {
            formData.append(`${key}Path`, val);
          }
        };

        Object.keys(localValues.value || {}).forEach((property) => {
          appendValue(property, localValues.value[property]);
        });

        store.dispatch(Actions.SAVE_ENTITY, { data: formData, key: props.routeKey });
      } else {
        store.dispatch(Actions.SAVE_ENTITY, { data: localValues.value, key: props.routeKey });
      }
    };

    const onInvalidSubmit = ({ errors }: any) => {
      console.warn("Form validation failed:", errors);
      submitted.value = true;
    };

    const preventEnterSubmission = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      if (target.tagName.toLowerCase() !== "textarea") event.preventDefault();
    };

    const addTier = (fieldName: string, value?: number) => {
      const v = value ?? newTier.value;
      if (v != null && !isNaN(v)) {
        if (!Array.isArray(localValues.value[fieldName])) localValues.value[fieldName] = [];
        localValues.value[fieldName].push(v);
        newTier.value = null;
      }
    };

    const removeTier = (fieldName: string, index: number) => {
      const arr = localValues.value[fieldName];
      if (Array.isArray(arr)) arr.splice(index, 1);
    };

    const addNestedRow = (fieldName: string) => {
      const inputs = getFormMappingColumns.value || [];
      const nestedInput = Array.isArray(inputs) ? inputs.find((i: any) => i.name === fieldName) : undefined;
      const columns = nestedInput?.columns || [];

      const row = columns.reduce((acc: Record<string, any>, col: any) => {
        const key = col.valueAttr || col.name;
        if (Object.prototype.hasOwnProperty.call(col, "defaultValue")) {
          acc[key] = typeof col.defaultValue === "function" ? col.defaultValue() : col.defaultValue;
        } else if (col.value === "multiple") {
          acc[key] = [];
        } else {
          acc[key] = col.type === "checkbox" ? false : "";
        }
        return acc;
      }, {} as Record<string, any>);

      if (!Array.isArray(localValues.value[fieldName])) localValues.value[fieldName] = [];
      localValues.value[fieldName].push(row);
    };

    const removeNestedRow = (fieldName: string, index: number) => {
      const arr = localValues.value[fieldName];
      if (Array.isArray(arr)) arr.splice(index, 1);
    };

    return {
      getFormMappingColumns,
      getRelationships,
      getValidationSchema,
      getIsPage,
      localValues,
      submit,
      onInvalidSubmit,
      preventEnterSubmission,
      addTier,
      removeTier,
      addNestedRow,
      removeNestedRow,
      item,
      sideBar
    };
  },
});
